/**
 * A lista dos seletores do Desk Cloud (o clipe do chat, o "Todo o Desk Cloud" do painel Arquivos, a
 * capa da mesa), andando pelas pastas como a página (docs/spec/desk-cloud.md): fora de pasta, todos os
 * arquivos, com as pastas da raiz em cima; numa pasta, as de dentro e os arquivos dela, e a busca
 * procura também nas de dentro. A busca sai quando a pessoa para de digitar; as páginas são de 100.
 */
import { useEffect, useState } from 'react';
import { api, type FileListQuery } from '../api.js';
import type { DeskFile, DeskFolder } from '../shared.js';
import { useApp } from '../store.js';
import { childFolders, folderPath } from './cloudModel.js';

/** A página que chegou e de qual pasta e busca ela é: a de outra não recebe o "Carregar mais". */
type Page = { key: string; files: DeskFile[]; total: number };

export type CloudListing = {
  /** a pasta aberta; null fora de pasta */
  folderId: string | null;
  /** da raiz até a pasta aberta */
  path: DeskFolder[];
  /** todas as pastas (para contar as de dentro de cada uma) */
  folders: DeskFolder[];
  /** as pastas mostradas em cima: as de dentro da aberta, nenhuma durante a busca */
  tiles: DeskFolder[];
  open: (folderId: string | null) => void;
  /** o texto da caixa de busca */
  query: string;
  setQuery: (query: string) => void;
  /** a busca já pedida ao servidor */
  q: string;
  /** null enquanto a pasta carrega */
  files: DeskFile[] | null;
  hasMore: boolean;
  loadingMore: boolean;
  more: () => void;
};

/** Que arquivos a lista traz: todos, só as imagens ou só os pacotes de plugin (.zip e .html). */
export type CloudListingOnly = 'images' | 'plugins' | null;

function listQuery(folderId: string | null, q: string, only: CloudListingOnly, offset: number): FileListQuery {
  return {
    view: folderId ? 'folder' : 'all',
    ...(folderId ? { folderId } : {}),
    ...(only === 'images' ? { images: true } : {}),
    ...(only === 'plugins' ? { plugins: true } : {}),
    ...(q ? { q } : {}),
    ...(offset ? { offset } : {}),
  };
}

/** `images`: só as imagens (a capa e a marca da mesa); `plugins`: só os pacotes ("Instalar plugin…"). */
export function useCloudListing({ images = false, plugins = false }: { images?: boolean; plugins?: boolean } = {}): CloudListing {
  const only: CloudListingOnly = images ? 'images' : plugins ? 'plugins' : null;
  const token = useApp((s) => s.token);
  const [folders, setFolders] = useState<DeskFolder[]>([]);
  const [folderId, setFolderId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState<Page | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const key = `${folderId ?? ''}/${q}`;

  // as pastas vêm do resumo da lateral, uma vez: o seletor fica aberto pouco tempo
  useEffect(() => {
    if (!token) return;
    let alive = true;
    api.fileSummary(token).then(
      (summary) => alive && setFolders(summary.folders),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [token]);

  useEffect(() => {
    const timer = window.setTimeout(() => setQ(query.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [query]);

  // a busca nova deixa a lista de antes até a resposta chegar; a pasta nova começa do zero (`open`)
  useEffect(() => {
    if (!token) return;
    let alive = true;
    const at = `${folderId ?? ''}/${q}`;
    api.listFiles(token, listQuery(folderId, q, only, 0)).then(
      (result) => alive && setPage({ key: at, files: result.files, total: result.total }),
      () => alive && setPage({ key: at, files: [], total: 0 }),
    );
    return () => {
      alive = false;
    };
  }, [token, folderId, q, only]);

  const open = (id: string | null) => {
    if (id === folderId) return;
    setFolderId(id);
    setQuery('');
    setQ('');
    setPage(null);
  };

  const more = () => {
    if (!token || !page || page.key !== key || loadingMore || page.files.length >= page.total) return;
    setLoadingMore(true);
    api
      .listFiles(token, listQuery(folderId, q, only, page.files.length))
      .then((result) =>
        setPage((current) => {
          if (!current || current.key !== key) return current;
          const known = new Set(current.files.map((file) => file.id));
          return { key, files: [...current.files, ...result.files.filter((file) => !known.has(file.id))], total: result.total };
        }),
      )
      .catch(() => undefined)
      .finally(() => setLoadingMore(false));
  };

  return {
    folderId,
    path: folderId ? folderPath(folders, folderId) : [],
    folders,
    tiles: q ? [] : childFolders(folders, folderId),
    open,
    query,
    setQuery,
    q,
    files: page?.files ?? null,
    hasMore: !!page && page.key === key && page.files.length < page.total,
    loadingMore,
    more,
  };
}
